import { apiClient } from "../../../lib/api";

export type Answer = string | number | string[];
export interface AssessmentQuestion {
  id: string;
  prompt: string;
  help: string;
  type: "text" | "choice" | "multi" | "number";
  required: boolean;
  competency: string;
  max_points: number;
  options: { id: string; label: string; points: number; flag: boolean }[];
  rubric: string[];
  answer?: number;
  tolerance?: number;
  condition?: { setting: string; equals: boolean };
}
export interface AssessmentDefinition {
  title: string;
  welcome: string;
  estimated_minutes: number;
  interview: string[];
  sections: {
    id: string;
    title: string;
    weight: number;
    questions: AssessmentQuestion[];
  }[];
}
export interface AssessmentTemplate {
  id: number;
  name: string;
  draft: AssessmentDefinition;
  archived: boolean;
  updated_at: string;
  versions: { id: number; number: number; title: string }[];
}
export interface AssessmentSettings {
  driving_required?: boolean;
  role_details?: string;
  help_contact?: string;
}
export interface AssessmentSummary {
  id: string;
  candidate_id: string | null;
  name: string;
  email: string;
  status: string;
  revision: number;
  version_id: number;
  version_number: number;
  title: string;
  selected: boolean;
  started_at: string;
  last_saved_at: string;
  submitted_at: string | null;
  last_invited_at: string | null;
  expires_at: string;
  result: {
    score: number | null;
    pending: number;
    reviewed: number;
    scored_questions: number;
    answered: number;
    total: number;
    flags: { question_id: string; prompt: string; competency: string }[];
    sections: {
      id: string;
      title: string;
      weight: number;
      score: number | null;
      pending: number;
    }[];
    questions: {
      id: string;
      points: number | null;
      max_points: number;
      flag: boolean;
    }[];
  };
}
export type Ratings = Record<string, { criteria: number[]; notes: string }>;
export interface AssessmentReport extends AssessmentSummary {
  definition: AssessmentDefinition;
  answers: Record<string, Answer>;
  ratings: Ratings;
  events: {
    kind: string;
    data: Record<string, unknown>;
    created_at: string;
    actor__first_name: string;
    actor__last_name: string;
  }[];
  delivery_accepted?: boolean;
}
export interface AssessmentCampaign {
  versions: {version_id: number; version__number: number; version__definition__title: string}[];
  updated_at: string;
  version_id: number | null;
  settings: AssessmentSettings;
  can_edit: boolean;
  counts: Record<string, number>;
  count: number;
  attempts: AssessmentSummary[];
}
export interface AssessmentSession {
  id: string;
  token?: string;
  status: string;
  revision: number;
  first_name: string;
  last_name: string;
  email: string;
  applied: boolean;
  answers: Record<string, Answer>;
  definition: AssessmentDefinition;
  settings: AssessmentSettings;
  expires_at: string;
  email_sent?: boolean;
  detail?: string;
}
export const assessmentApi = {
  async get<T>(path: string) {
    return (await apiClient.get<T>(`/api/hr/recruiting/${path}`)).data;
  },
  async post<T>(path: string, data: unknown) {
    return (await apiClient.post<T>(`/api/hr/recruiting/${path}`, data)).data;
  },
  async put<T>(path: string, data: unknown) {
    return (await apiClient.put<T>(`/api/hr/recruiting/${path}`, data)).data;
  },
};
function readableError(data: unknown): string {
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.map(readableError).join(" ");
  if (data && typeof data === "object")
    return Object.entries(data)
      .map(
        ([key, value]) =>
          `${key === "detail" || key === "non_field_errors" ? "" : `${key.replaceAll("_", " ")}: `}${readableError(value)}`,
      )
      .join(" ");
  return "Unable to save. Please try again.";
}
export function errorText(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data) return readableError(data);
  return error instanceof Error
    ? error.message
    : "Unable to save. Please try again.";
}
export async function publicAssessment<T>(
  slug: string,
  action: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(
    `/api/hr/recruiting/apply/${encodeURIComponent(slug)}/assessment/${action}/`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const data = await response.json();
  if (!response.ok) throw new Error(readableError(data));
  return data as T;
}
export const statusLabel = (status: string) =>
  ({
    registered: "Not started",
    in_progress: "In progress",
    submitted: "Needs review",
    reviewed: "Scored",
    withdrawn: "Withdrawn",
  })[status] || status;
